import React, { useState, useMemo } from 'react';
import { ArrowLeft, Check, ChevronRight, ChevronDown, Users, Leaf, Waves, TreePine, Briefcase } from 'lucide-react';
import { useContent, normalizeContentLang } from '../services/contentService';
import { useI18n } from '../i18n';
import BackgroundStack from './background/BackgroundStack';
import LanguageSelector from './LanguageSelector';
import ScenarioDescription from './ScenarioDescription';

// Paleta fija de iconos — se asigna por índice del ID del escenario para que
// sea estable entre idiomas y no repita iconos consecutivos.
const SCENARIO_ICON_PALETTE = [
    { Icon: Leaf,      bg: '#1A4731', color: '#6EE7B7' },
    { Icon: Waves,     bg: '#1E3A5F', color: '#93C5FD' },
    { Icon: TreePine,  bg: '#2D5016', color: '#86EFAC' },
    { Icon: Briefcase, bg: '#3B2F2F', color: '#FCA5A5' },
    { Icon: Users,     bg: '#4A1942', color: '#F0ABFC' },
];

const getScenarioIcon = (id, allIds) => {
    const index = allIds.indexOf(id);
    return SCENARIO_ICON_PALETTE[index % SCENARIO_ICON_PALETTE.length];
};

const ScenarioSelector = ({
    selectedScenario,
    onScenarioSelect,
    selectedLevel,
    onLevelChange,
    onContinue,
    onBack,
    selectedLanguage,
    onLanguageChange,
}) => {
    const { content } = useContent();
    const { t } = useI18n();
    const contentSlice = useMemo(() => {
        if (!content?.byLang) return content;
        const L = normalizeContentLang(selectedLanguage);
        return content.byLang[L] || content.byLang.es;
    }, [content, selectedLanguage]);
    const LEVELS = [
        {
            id: 1,
            label: t('basic'),
            dashCount: 2,
            time: '~10 min',
            desafios: t('challenges', { n: 2 }),
            description: t('basicDesc'),
        },
        {
            id: 2,
            label: t('intermediate'),
            dashCount: 3,
            time: '~15 min',
            desafios: t('challenges', { n: 2 }),
            description: t('intermediateDesc'),
        },
        {
            id: 3,
            label: t('advanced'),
            dashCount: 3,
            time: '~22 min',
            desafios: t('challenges', { n: 2 }),
            description: t('advancedDesc'),
        },
    ];
    const ESCENARIOS = contentSlice?.escenarios || {};
    const scenarios = Object.entries(ESCENARIOS);
    const scenarioIds = scenarios.map(([id]) => id);
    const [expandedScenario, setExpandedScenario] = useState(null);
    const [hoveredScenario, setHoveredScenario] = useState(null);

    const handleScenarioClick = (id, scenario) => {
        if (expandedScenario === id) {
            setExpandedScenario(null);
        } else {
            setExpandedScenario(id);
            onScenarioSelect(id, scenario);
        }
    };

    const canContinue = selectedScenario && selectedLevel;
    const selectedScenarioData = selectedScenario ? ESCENARIOS[selectedScenario] : null;
    const selectedLevelData = selectedLevel ? LEVELS.find(l => l.id === selectedLevel) : null;

    return (
        <BackgroundStack className="min-h-screen w-full" sidebarExpanded={false} scrollable>

            {/* Header */}
            <div className="bg-white border-b border-stone-200">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#1A4731' }}>
                                <span className="text-white font-bold text-sm">S</span>
                            </div>
                            <div>
                                <span className="font-semibold text-stone-800 text-sm">SimAI</span>
                                <span className="text-stone-400 text-sm mx-2">|</span>
                                <span className="text-stone-500 text-sm">{t('idbAse')}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {selectedLanguage && onLanguageChange && (
                            <LanguageSelector selectedLanguage={selectedLanguage} onLanguageChange={onLanguageChange} />
                        )}
                        <button
                            onClick={onBack}
                            className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 transition-colors px-3 py-1.5 rounded-lg hover:bg-stone-100"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            {t('back')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Progress */}
            <div className="bg-white border-b border-stone-200 px-6 py-3">
                <div className="max-w-4xl mx-auto">
                    <div className="flex items-center gap-4">
                        <span className="text-xs text-stone-500 whitespace-nowrap">{t('stepXofY', { x: 1, y: 3 })}</span>
                        <div className="flex-1 flex items-center gap-0.5">
                            <div className="h-1.5 rounded-full flex-1" style={{ background: '#1A4731' }} />
                            <div className="h-1.5 rounded-full flex-1" style={{ background: '#D6D0C4' }} />
                            <div className="h-1.5 rounded-full flex-1" style={{ background: '#D6D0C4' }} />
                        </div>
                        <div className="flex items-center gap-4 text-xs">
                            <span className="font-semibold text-stone-800">{t('scenario')}</span>
                            <span className="text-stone-400">{t('skill')}</span>
                            <span className="text-stone-400">{t('simulation')}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-6 py-8 pb-32">

                {/* Title */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-stone-900 mb-2">{t('chooseScenario')}</h1>
                    <p className="text-stone-500 text-sm">{t('chooseScenarioDesc')}</p>
                </div>

                {/* ESCENARIO section */}
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold uppercase tracking-widest text-stone-400">{t('scenario')}</span>
                        <span className="text-xs text-stone-400">{t('nAvailable', { n: scenarios.length })}</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {scenarios.map(([id, scenario]) => {
                            const isSelected = selectedScenario === id;
                            const isExpanded = expandedScenario === id;
                            const { Icon, bg, color } = getScenarioIcon(id, scenarioIds);
                            const sectorLabel = (scenario.tipo_proyecto || '').split('–')[0].split('-')[0].trim();

                            return (
                                <div key={id} className="relative">
                                    {/* Hover tooltip */}
                                    {hoveredScenario === id && !isExpanded && !isSelected && (
                                        <div className="absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 bg-white rounded-xl shadow-xl border border-stone-200 p-4 pointer-events-none">
                                            <p className="text-xs text-stone-500 mb-1 uppercase tracking-wider">{scenario.tipo_proyecto}</p>
                                            <p className="font-semibold text-stone-800 text-sm mb-2">{scenario.nombre}</p>
                                            <p className="text-xs text-stone-500 leading-relaxed line-clamp-3">{scenario.descripcion_breve}</p>
                                            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-stone-100">
                                                <Users className="w-3 h-3 text-stone-400" />
                                                <span className="text-xs text-stone-400">{t('clickForDetail')}</span>
                                            </div>
                                        </div>
                                    )}

                                    <button
                                        onClick={() => handleScenarioClick(id, scenario)}
                                        onMouseEnter={() => setHoveredScenario(id)}
                                        onMouseLeave={() => setHoveredScenario(null)}
                                        className={`w-full text-left rounded-xl p-4 border-2 transition-all duration-200 ${
                                            isSelected
                                                ? 'border-stone-700 bg-white shadow-md'
                                                : 'border-stone-200 bg-white hover:border-stone-300 hover:shadow-sm'
                                        }`}
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div
                                                className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                                                style={{ background: bg }}
                                            >
                                                <Icon className="w-5 h-5" style={{ color }} />
                                            </div>
                                            {isSelected && (
                                                <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: '#1A4731' }}>
                                                    <Check className="w-3 h-3 text-white" />
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-[10px] uppercase tracking-widest text-stone-400 mb-1 font-medium">{sectorLabel}</p>
                                        <p className="font-semibold text-stone-800 text-sm leading-snug">{scenario.nombre}</p>
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    {/* Expanded detail panel */}
                    {expandedScenario && ESCENARIOS[expandedScenario] && (() => {
                        const sc = ESCENARIOS[expandedScenario];
                        const { Icon, bg, color } = getScenarioIcon(expandedScenario, scenarioIds);
                        const sectorLabel = (sc.tipo_proyecto || '').split('–')[0].split('-')[0].trim();
                        return (
                            <div className="mt-3 bg-white rounded-2xl border-2 border-stone-200 overflow-hidden">
                                <div className="p-6">
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: bg }}>
                                            <Icon className="w-6 h-6" style={{ color }} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-[10px] uppercase tracking-widest text-stone-400 font-medium">{sectorLabel}</span>
                                                <span className="text-[10px] bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full font-medium">{t('selectedCase')}</span>
                                            </div>
                                            <h3 className="text-xl font-bold text-stone-900">{sc.nombre}</h3>
                                        </div>
                                        <button
                                            onClick={() => setExpandedScenario(null)}
                                            className="text-stone-400 hover:text-stone-600 p-1"
                                        >
                                            <ChevronDown className="w-4 h-4" />
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-5">
                                        <div className="md:col-span-2 space-y-4">
                                            <div>
                                                <p className="text-[10px] uppercase tracking-widest text-stone-400 font-semibold mb-2">{t('context')}</p>
                                                <ScenarioDescription
                                                    text={sc.descripcion_detallada || sc.descripcion_breve}
                                                    className="text-sm text-stone-600"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-3">
                                            <div className="bg-stone-50 rounded-xl p-4">
                                                <p className="text-[10px] uppercase tracking-widest text-stone-400 font-semibold mb-2">{t('yourInterlocutorLabel')}</p>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center">
                                                        <Users className="w-4 h-4 text-stone-500" />
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-semibold text-stone-700">{sc.nombre_personaje || t('projectRepresentative')}</p>
                                                        <p className="text-[11px] text-stone-400">{sc.cargo_personaje || sc.tipo_proyecto}</p>
                                                    </div>
                                                </div>
                                                <div className="space-y-1.5 mt-3 text-[11px]">
                                                    <div className="flex justify-between text-stone-500">
                                                        <span>{t('challengesLabel')}</span>
                                                        <span className="font-medium text-stone-700">2</span>
                                                    </div>
                                                    <div className="flex justify-between text-stone-500">
                                                        <span>{t('mode')}</span>
                                                        <span className="font-medium text-stone-700">{t('voiceAndText')}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Nivel de dificultad — dentro del panel para evitar scroll en móvil */}
                                    <div className="mt-6 pt-5 border-t border-stone-100">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-xs font-semibold uppercase tracking-widest text-stone-400">{t('difficultyLevel')}</span>
                                            <span className="text-xs text-stone-400">{t('selectOne')}</span>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                            {LEVELS.map((level) => {
                                                const isSelected = selectedLevel === level.id;
                                                return (
                                                    <button
                                                        key={level.id}
                                                        onClick={() => onLevelChange(level.id)}
                                                        className={`text-left rounded-xl p-3 border-2 transition-all duration-200 ${
                                                            isSelected
                                                                ? 'border-stone-700 bg-stone-50 shadow-sm'
                                                                : 'border-stone-200 bg-white hover:border-stone-300'
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-between mb-1">
                                                            <span className="font-semibold text-stone-800 text-sm">{level.label}</span>
                                                            <div className="flex gap-0.5">
                                                                {Array.from({ length: 3 }).map((_, i) => (
                                                                    <div
                                                                        key={i}
                                                                        className="h-0.5 w-4 rounded-full"
                                                                        style={{ background: i < level.dashCount ? (isSelected ? '#1A4731' : '#9CA3AF') : '#E5E7EB' }}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </div>
                                                        <p className="text-[11px] text-stone-400">{level.time} · {level.desafios}</p>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                                <div className="border-t border-stone-100 px-6 py-4 flex items-center justify-between bg-stone-50">
                                    <button
                                        onClick={() => setExpandedScenario(null)}
                                        className="flex items-center gap-1.5 text-sm font-medium text-stone-500 hover:text-stone-800 transition-colors"
                                    >
                                        {t('backToOptions')}
                                    </button>
                                    <button
                                        onClick={onContinue}
                                        disabled={!canContinue}
                                        className={`flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold transition-all ${
                                            canContinue
                                                ? 'text-white hover:opacity-90 hover:scale-[1.02]'
                                                : 'bg-stone-200 text-stone-400 cursor-not-allowed'
                                        }`}
                                        style={canContinue ? { background: '#1A4731' } : undefined}
                                    >
                                        {t('continue')}
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        );
                    })()}
                </div>

                {/* Nivel standalone — solo visible cuando ningún escenario está expandido */}
                {!expandedScenario && (
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold uppercase tracking-widest text-stone-400">{t('difficultyLevel')}</span>
                        <span className="text-xs text-stone-400">{t('selectOne')}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {LEVELS.map((level) => {
                            const isSelected = selectedLevel === level.id;
                            return (
                                <button
                                    key={level.id}
                                    onClick={() => onLevelChange(level.id)}
                                    className={`text-left rounded-xl p-4 border-2 transition-all duration-200 ${
                                        isSelected
                                            ? 'border-stone-700 bg-white shadow-md'
                                            : 'border-stone-200 bg-white hover:border-stone-300 hover:shadow-sm'
                                    }`}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="font-semibold text-stone-800 text-sm">{level.label}</span>
                                        <div className="flex gap-0.5">
                                            {Array.from({ length: 3 }).map((_, i) => (
                                                <div
                                                    key={i}
                                                    className="h-0.5 w-4 rounded-full"
                                                    style={{
                                                        background: i < level.dashCount
                                                            ? (isSelected ? '#1A4731' : '#9CA3AF')
                                                            : '#E5E7EB'
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                    <p className="text-[11px] text-stone-400 mb-2">{level.time} · {level.desafios}</p>
                                    <p className="text-xs text-stone-500 leading-relaxed">{level.description}</p>
                                </button>
                            );
                        })}
                    </div>
                </div>
                )}
            </div>

            {/* Bottom summary bar */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 z-10">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
                    {canContinue ? (
                        <>
                            <div className="flex items-center gap-3 flex-1">
                                {selectedScenarioData && (() => {
                                    const { Icon, bg, color } = getScenarioIcon(selectedScenario, scenarioIds);
                                    return (
                                        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: bg }}>
                                            <Icon className="w-4 h-4" style={{ color }} />
                                        </div>
                                    );
                                })()}
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-stone-800 truncate">
                                        {selectedScenarioData?.nombre} · {selectedLevelData?.label}
                                    </p>
                                    <p className="text-xs text-stone-400">{selectedLevelData?.time} · {selectedLevelData?.desafios}</p>
                                </div>
                            </div>
                            <button
                                onClick={onContinue}
                                className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 hover:scale-[1.02] shrink-0"
                                style={{ background: '#1A4731' }}
                            >
                                {t('continue')}
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </>
                    ) : (
                        <p className="text-sm text-stone-400 flex-1">
                            {!selectedScenario ? t('selectScenario') : t('selectLevel')}
                        </p>
                    )}
                </div>
            </div>
        </BackgroundStack>
    );
};

export default ScenarioSelector;
